import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { categories } from '@/mocks/categories';
import { movies } from '@/mocks/movies';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mitaomovie.com/' },
    { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://mitaomovie.com/category/' },
  ],
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'aiqing';
  const [activeSlug, setActiveSlug] = useState(currentSlug);

  useEffect(() => {
    setActiveSlug(currentSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const filteredMovies = movies.filter((m) => m.category === activeSlug);

  const schema = {
    ...breadcrumbSchema,
  };
  if (currentCategory) {
    schema.itemListElement.push({
      '@type': 'ListItem',
      position: 3,
      name: currentCategory.name,
    });
  }

  return (
    <>
      <SEO
        title={`${currentCategory?.name || '分类'} - 蜜桃电影精选推荐`}
        description={currentCategory?.description || '蜜桃电影分类浏览，按类型发现好片'}
        keywords={`蜜桃电影,${currentCategory?.name || ''}片,在线观看推荐,高清资源,热门推荐`}
        canonicalUrl={`https://mitaomovie.com/category/${activeSlug}`}
        ogType="website"
        schema={schema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '分类浏览' },
                { label: currentCategory?.name || '' },
              ]}
            />
          </div>

          {/* Category Header */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                {currentCategory?.name}电影推荐
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                {currentCategory?.description}
              </p>
            </div>
          </section>

          {/* Tab Switcher */}
          <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
              <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide flex-nowrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Movie Grid */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6" data-product-shop>
                  {filteredMovies.map((movie) => (
                    <MovieCard
                      key={movie.id}
                      id={movie.id}
                      title={movie.title}
                      titleEn={movie.titleEn}
                      category={movie.category}
                      year={movie.year}
                      rating={movie.rating}
                      posterImage={movie.posterImage}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200">
                    <i className="ri-movie-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无影片，敬请期待新片上线</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}